import { DataType, InputValueInput } from "src/core/gql/graphql";
import {
  AssignmentMode,
  ExternalDataMismatchError,
  ImportMessageCode,
  ImportRow,
} from "src/core/utils/import/types";

const PLACEHOLDER_CORPORATION_NAME = "Raison sociale non-renseignée";
const PLACEHOLDER_PERSON_NAME = "Non-renseigné";

export type CorporationSearchResult = {
  exists: boolean;
  isCustomer: boolean;
  corporationId: string | null;
};

export type PersonSearchResult = {
  exists: boolean;
  personId: string | null;
};

export type AutoCreateResult = {
  success: boolean;
  customerId: string | null;
};

export type CreateFolderResult = {
  success: boolean;
  customerFolderId: string | null;
  folderFileId: string | null;
};

export type ImportApi = {
  searchCorporation: (siren: string) => Promise<CorporationSearchResult>;
  searchPerson: (siren: string) => Promise<PersonSearchResult>;
  createAuto: (siren: string, isPerson: boolean) => Promise<AutoCreateResult>;
  createFolder: (input: {
    siren: string;
    entityType: string;
    isPerson: boolean;
    creationAuto: boolean;
    customerId: string | null;
    idOwner: string | null;
  }) => Promise<CreateFolderResult>;
  callEReputation: (folderFileId: string) => Promise<boolean>;
  followFolder: (customerFolderId: string, userId: string) => Promise<boolean>;
};

const withSteps = (
  row: ImportRow,
  patch: Partial<ImportRow> & { messageCodes?: ImportMessageCode[] }
): ImportRow => ({
  ...row,
  messageCodes: patch.messageCodes ?? row.messageCodes.filter((code) => code !== "duplicate_siren"),
  ...patch,
});

const stopRow = (
  row: ImportRow,
  existant: boolean | null,
  code: ImportMessageCode,
  detail: string | null
): ImportRow =>
  withSteps(row, {
    status: "error",
    existant,
    creationAuto: false,
    creationFolder: false,
    callEReputation: false,
    callFollowFolder: false,
    messageCodes: [code],
    messageDetail: detail,
  });

/**
 * Reprend le flux de GraphQLClientService::processRow.
 * Une personne trouvée est traitée comme déjà cliente.
 * Le mismatch de données externes stoppe les étapes suivantes.
 */
export const processImportRow = async (
  row: ImportRow,
  mode: AssignmentMode,
  api: ImportApi
): Promise<ImportRow> => {
  if (!row.isValid) return row;

  const isPerson = row.entityType === "person";
  let existant = false;
  let isCustomer = false;
  let customerId: string | null = null;

  try {
    if (isPerson) {
      const person = await api.searchPerson(row.siren);
      existant = person.exists;
      isCustomer = true;
      customerId = person.personId;
    } else {
      const corporation = await api.searchCorporation(row.siren);
      existant = corporation.exists;
      isCustomer = corporation.isCustomer;
      customerId = corporation.corporationId;
    }

    if (existant && isCustomer) {
      return withSteps(row, {
        status: "existing",
        existant: true,
        creationAuto: null,
        creationFolder: null,
        callEReputation: null,
        callFollowFolder: null,
        messageCodes: [],
        messageDetail: null,
      });
    }

    let creationAuto = false;

    if (isCustomer) {
      const auto = await api.createAuto(row.siren, isPerson);
      if (!auto.success || !auto.customerId) {
        return withSteps(row, {
          status: "error",
          existant,
          creationAuto: false,
          creationFolder: null,
          callEReputation: null,
          callFollowFolder: null,
          messageCodes: ["auto_failed"],
          messageDetail: null,
        });
      }
      creationAuto = true;
      customerId = auto.customerId;
    } else if (customerId) {
      creationAuto = true;
    } else {
      return stopRow(row, existant, "api_error", null);
    }

    const folder = await api.createFolder({
      siren: row.siren,
      entityType: row.entityType,
      isPerson,
      creationAuto,
      customerId,
      idOwner: row.idOwner,
    });

    let callEReputation: boolean | null = null;
    if (creationAuto && folder.success && folder.folderFileId) {
      callEReputation = await api.callEReputation(folder.folderFileId);
    } else if (creationAuto && folder.success) {
      callEReputation = false;
    }

    let callFollowFolder: boolean | null = null;
    const shouldFollow = mode === "with" && row.assigneeIds.length > 0;
    if (folder.success && folder.customerFolderId && shouldFollow) {
      const results = await Promise.all(
        row.assigneeIds.map((userId) => api.followFolder(folder.customerFolderId as string, userId))
      );
      callFollowFolder = results.every(Boolean);
    } else if (folder.success && mode === "with") {
      callFollowFolder = true;
    }

    const messageCodes: ImportMessageCode[] = [];
    if (!folder.success) messageCodes.push("folder_failed");
    if (folder.success && creationAuto && callEReputation !== true) {
      messageCodes.push("ereputation_failed");
    }
    if (folder.success && mode === "with" && callFollowFolder !== true) {
      messageCodes.push("follow_failed");
    }

    const created =
      folder.success &&
      creationAuto &&
      callEReputation === true &&
      (mode === "without" || callFollowFolder === true);

    return withSteps(row, {
      status: created ? "created" : "error",
      existant,
      creationAuto,
      creationFolder: folder.success,
      callEReputation,
      callFollowFolder,
      messageCodes,
      messageDetail: null,
      customerFolderId: folder.customerFolderId,
      folderFileId: folder.folderFileId,
    });
  } catch (error) {
    if (error instanceof ExternalDataMismatchError) {
      return stopRow(row, existant, "external_mismatch", error.message);
    }

    const detail = error instanceof Error ? error.message : null;
    return stopRow(row, existant, "api_error", detail);
  }
};

export const buildCorporationFallbackInput = (
  siren: string,
  entityType: string
): InputValueInput[] => [
  { fieldCode: "name", value: PLACEHOLDER_CORPORATION_NAME, dataType: DataType.String },
  { fieldCode: "entityTypeCode", value: entityType, dataType: DataType.Code },
  { fieldCode: "registration", value: siren, dataType: DataType.String },
];

export const buildPersonFallbackInput = (siren: string): InputValueInput[] => [
  { fieldCode: "firstName", value: PLACEHOLDER_PERSON_NAME, dataType: DataType.String },
  { fieldCode: "lastName", value: PLACEHOLDER_PERSON_NAME, dataType: DataType.String },
  { fieldCode: "registration", value: siren, dataType: DataType.String },
];
